/**
 * ResourceManager - Менеджер для управління ресурсами додатку
 *
 * Забезпечує централізоване управління ресурсами з типізацією,
 * ледачою ініціалізацією та залежностями
 *
 * @example
 * ```typescript
 * // Використання синглтону
 * import { resourceManager } from '@/Core/Managers/Resource'
 *
 * // Встановлення ресурсів
 * resourceManager.set('products', ProductResource)
 * resourceManager.sets({ products: ProductResource, users: UserResource })
 *
 * // Отримання ресурсу
 * const productResource = resourceManager.get('products')
 *
 * // Реєстрація з фабрикою
 * resourceManager.register('orders', () => createOrderResource(), { singleton: true })
 * ```
 */

import type {
    IResourceManager,
    ResourceDefinition,
    ResourceFactory,
    ResourceManagerOptions,
} from '@/Core/Contracts/Managers/IResourceManager'
import type { IResourceConfig } from '@/Core/Contracts/UI/Resource'
import { action, makeObservable } from 'mobx'

export class ResourceManager implements IResourceManager {
    private _resources = new Map<string, IResourceConfig>()
    private _definitions = new Map<string, ResourceDefinition>()
    private _instances = new Map<string, IResourceConfig>()
    private debug: boolean
    private context: Record<string, any>

    constructor(options: ResourceManagerOptions = {}) {
        this.debug = options.debug ?? false
        this.context = options.context ?? {}

        makeObservable(this, {
            sets: action,
            set: action,
            register: action,
            clear: action,
            clearInstances: action,
        })
    }

    /**
     * Встановлює множину ресурсів з об'єкта
     */
    sets(resources: Record<string, IResourceConfig>): void {
        if (typeof resources !== 'object' || resources === null) {
            if (this.debug) {
                console.warn('[ResourceManager] sets: Передані некоректні дані', resources)
            }
            return
        }

        for (const key in resources) {
            if (resources.hasOwnProperty(key)) {
                this._resources.set(key, resources[key])
            }
        }
    }

    /**
     * Встановлює один ресурс
     */
    set(key: string, resource: IResourceConfig): void {
        this._resources.set(key, resource)
    }

    /**
     * Реєструє ресурс з фабрикою для ледачої ініціалізації
     */
    register<T = IResourceConfig>(
        key: string,
        factory: ResourceFactory<T>,
        options: { singleton?: boolean; dependencies?: string[] } = {}
    ): void {
        const definition: ResourceDefinition<T> = {
            factory,
            singleton: options.singleton ?? true,
            dependencies: options.dependencies ?? [],
        }

        this._definitions.set(key, definition as ResourceDefinition)
    }

    /**
     * Отримує ресурс за ключем
     */
    get<T = IResourceConfig>(key: string, defaultValue?: T): T | undefined {
        // Спочатку перевіряємо звичайні ресурси
        if (this._resources.has(key)) {
            return this._resources.get(key) as T
        }

        // Потім перевіряємо зареєстровані ресурси
        if (this._definitions.has(key)) {
            return this._createResource<T>(key)
        }

        if (this.debug && defaultValue === undefined) {
            console.warn(`[ResourceManager] Ресурс не знайдено: ${key}`)
        }

        return defaultValue
    }

    /**
     * Створює або отримує екземпляр ресурсу
     */
    private _createResource<T>(key: string): T {
        const definition = this._definitions.get(key)
        if (!definition) {
            throw new Error(`Визначення ресурсу не знайдено: ${key}`)
        }

        // Якщо синглтон і вже створений - повертаємо існуючий
        if (definition.singleton && this._instances.has(key)) {
            return this._instances.get(key) as T
        }

        // Перевіряємо залежності
        const dependencies: any[] = []
        if (definition.dependencies && definition.dependencies.length > 0) {
            for (const depKey of definition.dependencies) {
                const dependency = this.get(depKey)
                if (dependency === undefined) {
                    throw new Error(`Залежність не знайдено: ${depKey} для ресурсу ${key}`)
                }
                dependencies.push(dependency)
            }
        }

        // Створюємо екземпляр
        const instance = definition.factory()

        // Зберігаємо для синглтонів
        if (definition.singleton) {
            this._instances.set(key, instance as IResourceConfig)
        }

        return instance as T
    }

    /**
     * Отримує всі ресурси у вигляді об'єкта
     */
    getAll(): Record<string, IResourceConfig> {
        const result: Record<string, IResourceConfig> = {}

        // Додаємо звичайні ресурси
        this._resources.forEach((resource: IResourceConfig, key: string) => {
            result[key] = resource
        })

        // Додаємо зареєстровані ресурси (тільки створені екземпляри)
        this._instances.forEach((instance: IResourceConfig, key: string) => {
            result[key] = instance
        })

        return result
    }

    /**
     * Перевіряє наявність ресурсу
     */
    has(key: string): boolean {
        return this._resources.has(key) || this._definitions.has(key)
    }

    /**
     * Видаляє ресурс
     */
    delete(key: string): boolean {
        const hasResource = this._resources.delete(key)
        const hasDefinition = this._definitions.delete(key)
        const hasInstance = this._instances.delete(key)

        const result = hasResource || hasDefinition || hasInstance

        return result
    }

    /**
     * Очищає всі ресурси
     */
    clear(): void {
        this._resources.clear()
        this._definitions.clear()
        this._instances.clear()
    }

    /**
     * Очищує тільки створені екземпляри (залишає визначення)
     */
    clearInstances(): void {
        this._instances.clear()
    }

    /**
     * Отримує кількість ресурсів
     */
    get size(): number {
        return this._resources.size + this._definitions.size
    }

    /**
     * Отримує всі ключі ресурсів
     */
    get keys(): string[] {
        const keys = new Set<string>()
        this._resources.forEach((_, key) => keys.add(key))
        this._definitions.forEach((_, key) => keys.add(key))
        return Array.from(keys)
    }

    /**
     * Отримує кількість створених екземплярів
     */
    get instancesCount(): number {
        return this._instances.size
    }

    /**
     * Отримує інформацію про зареєстровані ресурси
     */
    getRegisteredResources(): Record<string, { singleton: boolean; dependencies: string[] }> {
        const result: Record<string, { singleton: boolean; dependencies: string[] }> = {}

        this._definitions.forEach((definition, key) => {
            result[key] = {
                singleton: definition.singleton ?? true,
                dependencies: definition.dependencies ?? [],
            }
        })

        return result
    }

    /**
     * Перевіряє, чи порожній менеджер
     */
    get isEmpty(): boolean {
        return this._resources.size === 0 && this._definitions.size === 0
    }

    /**
     * Повертає JSON представлення всіх ресурсів
     */
    toJSON(): Record<string, IResourceConfig> {
        return this.getAll()
    }

    /**
     * Виводить інформацію про стан менеджера
     */
    debug_info(): void {
        console.group('[ResourceManager] Debug Info')
        console.log('Розмір:', this.size)
        console.log('Екземплярів створено:', this.instancesCount)
        console.log('Контекст:', this.context)
        console.log('Ресурси:', this.keys)
        console.log('Зареєстровані:', this.getRegisteredResources())
        console.groupEnd()
    }
}

/**
 * Фабрична функція для створення екземпляра ResourceManager
 */
export function createResourceManager(options?: ResourceManagerOptions): ResourceManager {
    return new ResourceManager(options)
}

/**
 * Глобальний екземпляр ResourceManager для загального використання
 */
const env = (import.meta as any).env
export const resourceManager = createResourceManager({
    debug: env?.DEV ?? false,
})

export default ResourceManager
